import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import shp from "shpjs";
import bbox from "@turf/bbox";
import * as turf from "@turf/turf";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { toastService } from "@services/ToastService";
import { useMapContext } from "@shared/components/Mapbox/MapContext"; // ✅ import context
import useCountryHighlight from "@pages/Project/utils/useCountryHighlight";

interface MapProps {
  onGeoJsonUpdate?: (geoJson: any) => void;
  originalGeoJson: any;
  isEditing: boolean;
  coordinates?: any;
  countryBorders?: any;
}

const getColor = (index: number): string => {
  const palette = [
    // Blue
    "#42A5F5", // ocean blue
    "#1E88E5", // strong blue

    // Green
    "#66BB6A", // medium green
    "#43A047", // strong green

    // Red
    "#EF5350", // coral red
    "#E53935", // bold red

    // Orange
    "#FF7043", // vivid orange
    "#FB8C00", // strong orange

    // Purple
    "#AB47BC", // rich purple
    "#8E24AA", // deep violet

    // Teal
    "#26A69A", // vibrant teal
    "#00897B", // jungle teal

    // Pink
    "#EC407A", // medium pink
    "#D81B60", // bright magenta

    // Indigo
    "#5C6BC0", // medium indigo
    "#3949AB", // vivid indigo
  ];

  return palette[index % palette.length];
};

const MapBox: React.FC<MapProps> = ({
  onGeoJsonUpdate = (geojson) => {
    return null;
  },
  originalGeoJson,
  isEditing,
  coordinates,
  countryBorders,
}) => {
  const { mapRef, mapContainerRef, drawRef, mapLoaded } = useMapContext(); // ✅ use context
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const [geoJsonState, setGeoJsonState] = useState<any>({
    type: "FeatureCollection",
    features: [],
  });

  const isDragging = useRef(false);
  const lastMousePos = useRef<{ x: number; y: number } | null>(null);
  const lastCountryIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;

    // ✅ Remove existing draw control if any
    if (drawRef.current) {
      try {
        mapRef.current.removeControl(drawRef.current);
      } catch (e) {
        console.warn("Tried to remove non-existent draw control:", e);
      }
    }

    // ✅ Create new draw control with controls based on isEditing
    drawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: isEditing
        ? { polygon: true, line_string: true, trash: true }
        : {},
    });

    // ✅ Assign the new draw control to drawRef

    // ✅ Add draw control to the map
    mapRef.current.addControl(drawRef.current, "top-left");

    // ✅ Bind events
    mapRef.current.on("draw.create", () => {
      if (isEditing) {
        onGeoJsonUpdate(drawRef.current!.getAll());
      }
    });

    mapRef.current.on("draw.update", () => {
      if (isEditing) {
        onGeoJsonUpdate(drawRef.current!.getAll());
      }
    });

    mapRef.current.on("draw.delete", () => {
      if (isEditing) {
        onGeoJsonUpdate(drawRef.current!.getAll());
      }
    });

    mapRef.current.on("click", handleShapeClick);

    // ✅ Draw existing geoJSON (if any)
    if (originalGeoJson) {
      addGeoJSONToDraw(originalGeoJson, countryBorders);
    }

    // ✅ Clear popup if editing is disabled
    if (!isEditing && popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
  }, [isEditing, mapLoaded]);

  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    if (!countryBorders || !countryBorders.features?.length) return;

    // wait one frame so layers/sources settle
    requestAnimationFrame(() => {
      try {
        const b = bbox(countryBorders) as [number, number, number, number];
        if (b.every((v) => Number.isFinite(v))) {
          mapRef.current!.fitBounds(b, { padding: 30, animate: false });
        }
      } catch (e) {
        // noop
      }
    });
  }, [countryBorders, mapLoaded]);

  const handleShapefileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        if (!e.target?.result) return;
        const geojson = await shp(e.target.result as ArrayBuffer);
        if (geojson && mapLoaded) {
          addGeoJSONToDraw(geojson, countryBorders);
          onGeoJsonUpdate(geojson);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error processing shapefile:", error);
      toastService.error(
        "Invalid Shapefile. Please upload a valid .shp ZIP file."
      );
    }
  };

  const handleShapeClick = (e: mapboxgl.MapMouseEvent) => {
    if (!mapRef.current || !drawRef.current) return;

    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    const allFeatures = drawRef.current.getAll().features;
    if (allFeatures.length === 0) return;

    const clickedFeature = allFeatures.find((feature) => {
      try {
        return turf?.booleanPointInPolygon(
          turf?.point([e?.lngLat?.lng, e?.lngLat?.lat]),
          feature as any
        );
      } catch (e) {
        return false;
      }
    });

    if (clickedFeature) {
      const properties = clickedFeature.properties || {};
      let content = "";

      Object.keys(properties).forEach((key) => {
        content += `<p style="font-size:0.7rem"><strong>${key}:</strong> ${
          properties[key] || "N/A"
        }</p>`;
      });

      if (content === "") return false;

      popupRef.current = new mapboxgl.Popup({ closeOnClick: true })
        .setLngLat(e.lngLat)
        .setHTML(content)
        .addTo(mapRef.current);
    }
  };

  const addGeoJSONToDraw = (
    geojsonArray: any,
    currentCountryBorders: GeoJSON.FeatureCollection
  ) => {
    const map = mapRef.current;
    const draw = drawRef.current;
    if (!map || !draw) return;

    let mergedGeoJSON: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
      type: "FeatureCollection",
      features: [],
    };

    if (Array.isArray(geojsonArray)) {
      geojsonArray.forEach((geojson) => {
        if (geojson.type === "FeatureCollection") {
          mergedGeoJSON.features.push(...geojson.features);
        }
      });
    } else if (geojsonArray?.type === "FeatureCollection") {
      mergedGeoJSON = geojsonArray;
    }

    setGeoJsonState(mergedGeoJSON);

    draw.deleteAll();

    if (!isEditing) {
      ["styled-fill-layer", "styled-line-layer"].forEach((layerId) => {
        if (map.getLayer(layerId)) {
          map.removeLayer(layerId);
        }
      });

      if (map.getSource("styled-geojson")) {
        map.removeSource("styled-geojson");
      }

      const coloredFeatures = mergedGeoJSON.features.map((feature, index) => ({
        ...feature,
        properties: {
          ...feature.properties,
          color: getColor(index),
        },
      }));

      const styledGeoJson: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
        type: "FeatureCollection",
        features: coloredFeatures,
      };

      if (styledGeoJson.features.length > 0) {
        map.addSource("styled-geojson", {
          type: "geojson",
          data: styledGeoJson,
        });

        map.addLayer({
          id: "styled-fill-layer",
          type: "fill",
          source: "styled-geojson",
          filter: ["==", "$type", "Polygon"],
          paint: {
            "fill-color": ["get", "color"],
            "fill-opacity": 0.4,
          },
        });

        map.addLayer({
          id: "styled-line-layer",
          type: "line",
          source: "styled-geojson",
          filter: ["==", "$type", "LineString"],
          paint: {
            "line-color": ["get", "color"],
            "line-width": 2,
          },
        });
      }
    }

    if (mergedGeoJSON.features.length === 0) {
      if (currentCountryBorders && currentCountryBorders.features?.length) {
        const bboxResult = bbox(currentCountryBorders);
        const isValid = bboxResult.every((v) => Number.isFinite(v));
        if (isValid) {
          map.fitBounds(bboxResult as [number, number, number, number], {
            padding: 20,
            animate: false,
          });
        }
      }
      return;
    } else {
      if (isEditing) {
        if (map.getLayer("styled-fill-layer")) {
          map.removeLayer("styled-fill-layer");
        }
        if (map.getLayer("styled-line-layer")) {
          map.removeLayer("styled-line-layer");
        }
        if (map.getSource("styled-geojson")) {
          map.removeSource("styled-geojson");
        }
      }

      draw.add(mergedGeoJSON);

      try {
        setTimeout(() => {
          const geoBounds = bbox(mergedGeoJSON).slice(0, 4) as [
            number,
            number,
            number,
            number
          ];
          map.fitBounds(geoBounds, { padding: 20, animate: false });
        }, 250);
      } catch (error) {
        console.warn("Unable to fit map to bounds:", error);
      }
    }
  };

  const handleZoomIn = () => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    lastMousePos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !mapRef.current || !lastMousePos.current) return;

    const zoomLevel = mapRef.current.getZoom();
    let movementFactor;

    if (zoomLevel <= 5) {
      movementFactor = 0.1 / (zoomLevel / 2);
    } else if (zoomLevel <= 9) {
      movementFactor = 0.1 / Math.pow(zoomLevel / 1.5, 2);
    } else {
      movementFactor = 0.1 / Math.pow(zoomLevel / 1.1, 3); // More drastic slowdown for zoom > 8
    }

    const deltaX = e.clientX - lastMousePos.current.x;
    const deltaY = e.clientY - lastMousePos.current.y;
    lastMousePos.current = { x: e.clientX, y: e.clientY };

    const center = mapRef.current.getCenter();
    const newLng = center.lng - deltaX * movementFactor;
    const newLat = center.lat + deltaY * movementFactor;

    mapRef.current.setCenter([newLng, newLat]);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    lastMousePos.current = null;
  };

  const hasInitialRenderOccurred = useRef(false);

  useEffect(() => {
    if (!mapRef.current || !coordinates) return;

    const isGeoJsonEmpty =
      !originalGeoJson || originalGeoJson?.features?.length === 0;

    // Initial render logic
    if (!hasInitialRenderOccurred.current) {
      hasInitialRenderOccurred.current = true;

      if (isGeoJsonEmpty) {
        mapRef.current.jumpTo({
          center: coordinates,
          zoom: 4,
        });
      }

      return;
    }

    // All subsequent changes: always jump to new coordinates
    mapRef.current.jumpTo({
      center: coordinates,
      zoom: 4,
    });
  }, [coordinates]);

  useEffect(() => {
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    if (drawRef.current && mapLoaded) {
      addGeoJSONToDraw(originalGeoJson, countryBorders);
    } else if (mapRef.current) {
      mapRef.current.jumpTo({
        center: [25.0, 0.0],
        zoom: 2,
      });
    }
  }, [isEditing, mapRef.current, drawRef.current, originalGeoJson]);

  useCountryHighlight(mapRef, countryBorders);

  return (
    <div className="relative h-full w-full">
      <div ref={mapContainerRef} className="h-full w-full" />

      {isEditing ? (
        <div className="absolute top-4 right-4 flex flex-col space-y-0 shadow-xl overflow-hidden rounded-[0.4rem]">
          <button
            className="bg-gray-50 w-8 h-8 text-black text-lg p-1 rounded-t-[0.4rem] border border-[#ddd] border-b-0 hover:bg-gray-200 flex items-center justify-center"
            onClick={(e) => {
              e.preventDefault();
              handleZoomIn();
            }}
          >
            <img src="/plus.svg" className="w-[18px] h-[18px]" alt="" />
          </button>
          <button
            className="bg-gray-50 w-8 h-8 text-black text-lg p-1 rounded-b-[0.4rem] border border-[#ddd] hover:bg-gray-200 flex items-center justify-center"
            onClick={(e) => {
              e.preventDefault();
              handleZoomOut();
            }}
          >
            <img src="/minus.svg" className="w-[18px] h-[18px]" alt="" />
          </button>
        </div>
      ) : (
        <div
          className="w-full h-full bg-[transparent] z-[11] absolute cursor-grab active:cursor-grabbing top-0 left-0"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Zoom Controls */}
          <div className="absolute top-4 right-4 flex flex-col space-y-0 shadow-xl overflow-hidden rounded-[0.4rem]">
            <button
              className="bg-gray-50 w-8 h-8 text-black text-lg p-1 rounded-t-[0.4rem] border border-[#ddd] border-b-0  hover:bg-gray-200 flex items-center justify-center"
              onClick={(e) => {
                e.preventDefault();
                handleZoomIn();
              }}
            >
              <img src="/plus.svg" className="w-[18px] h-[18px]" alt="" />
            </button>
            <button
              className="bg-gray-50 w-8 h-8 text-black text-lg p-1 rounded-b-[0.4rem] border border-[#ddd]  hover:bg-gray-200 flex items-center justify-center"
              onClick={(e) => {
                e.preventDefault();
                handleZoomOut();
              }}
            >
              <img src="/minus.svg" className="w-[18px] h-[18px]" alt="" />
            </button>
          </div>
        </div>
      )}
      {isEditing && (
        <div className="mt-4 border-2 border-dashed rounded-md py-6 text-center border-gray-300 absolute bottom-[-100px] w-full">
          <input
            type="file"
            id="shapeFileInput"
            accept=".zip"
            className="hidden"
            onChange={handleShapefileUpload}
          />
          <label
            htmlFor="shapeFileInput"
            className="text-blue-600 text-sm 2xl:text-base cursor-pointer hover:underline"
          >
            Upload shape files (.zip folder)
          </label>
        </div>
      )}
    </div>
  );
};

export default MapBox;
